/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from '../../i18n/useTranslation';
import { ShoppingAssistantMessage, ShoppingAssistantProduct, ShoppingAssistantOrder } from '../../shoppingAssistant';
import { detectIntent, ShoppingAssistantContext } from '../../services/shoppingAssistantService';
import { XIcon, SendIcon, ShoppingBagIcon, SparklesIcon, Trash2Icon, ShareIcon, CalendarIcon, CheckCircleIcon } from '../icons';
import { WardrobeItem, OutfitLayer, UserProfile } from '../../types';
import { ProductCarousel } from './ProductCarousel';
import { OrderCard } from './OrderCard';
import Spinner from '../Spinner';

interface ShoppingAssistantChatProps {
    isOpen: boolean;
    onClose: () => void;
    onAddToBasket: (product: any) => void;
    wardrobeItems?: WardrobeItem[];
    currentOutfit?: OutfitLayer;
    userProfile?: UserProfile;
    onSaveAdvice?: (advice: any) => void;
    initialQuery?: string;
    isInline?: boolean;
}

export const ShoppingAssistantChat: React.FC<ShoppingAssistantChatProps> = ({
    isOpen,
    onClose,
    onAddToBasket,
    wardrobeItems = [],
    currentOutfit,
    userProfile,
    onSaveAdvice,
    initialQuery,
    isInline = false
}) => {
    const { t } = useTranslation();
    const [messages, setMessages] = useState<ShoppingAssistantMessage[]>([]);
    const [input, setInput] = useState('');
    const [isThinking, setIsThinking] = useState(false);
    const chatEndRef = useRef<HTMLDivElement>(null);
    const sessionId = useRef(`session-${Date.now()}`);

    useEffect(() => {
        // Load history or greeting
        const savedHistory = localStorage.getItem('shoppingAssistantHistory');
        if (savedHistory) {
            setMessages(JSON.parse(savedHistory));
        } else {
            setMessages([
                {
                    id: 'init',
                    role: 'model',
                    textKey: "shoppingAssistant_greeting",
                    timestamp: Date.now()
                }
            ]);
        }
    }, []);

    useEffect(() => {
        if (isOpen && initialQuery && (messages.length === 0 || messages[0]?.id === 'init')) {
            handleSendMessage(undefined, initialQuery);
        }
    }, [isOpen, initialQuery]);

    useEffect(() => {
        if (messages.length > 0) {
            localStorage.setItem('shoppingAssistantHistory', JSON.stringify(messages));
        }
        chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages]);

    const handleSendMessage = async (e?: React.FormEvent, customText?: string) => {
        if (e) e.preventDefault();
        const textToSend = customText || input;
        if (!textToSend.trim() || isThinking) return;

        const userMsg: ShoppingAssistantMessage = {
            id: `user-${Date.now()}`,
            role: 'user',
            text: textToSend,
            timestamp: Date.now()
        };

        setMessages(prev => [...prev, userMsg]);
        if (!customText) setInput('');
        setIsThinking(true);

        try {
            const context: ShoppingAssistantContext = {
                wardrobeItems,
                currentOutfit,
                userProfile
            };
            const response = await detectIntent(textToSend, sessionId.current, context);
            setMessages(prev => [...prev, response]);
        } catch (error) {
            setMessages(prev => [...prev, {
                id: `error-${Date.now()}`,
                role: 'model',
                textKey: "shoppingAssistant_error_connection",
                timestamp: Date.now(),
                isError: true
            }]);
        } finally {
            setIsThinking(false);
        }
    };

    const handleClearHistory = () => {
        const initMsg: ShoppingAssistantMessage = {
            id: `init-${Date.now()}`,
            role: 'model',
            textKey: "shoppingAssistant_history_cleared",
            timestamp: Date.now()
        };
        setMessages([initMsg]);
        localStorage.removeItem('shoppingAssistantHistory');
    };

    if (!isOpen) return null;

    return (
        <motion.div
            initial={isInline ? { opacity: 0 } : { opacity: 0, x: 300 }}
            animate={isInline ? { opacity: 1 } : { opacity: 1, x: 0 }}
            exit={isInline ? { opacity: 0 } : { opacity: 0, x: 300 }}
            className={`flex flex-col ${isInline
                ? 'w-full h-[600px] rounded-2xl overflow-hidden border border-gray-200 dark:border-gray-700'
                : 'fixed inset-y-0 right-0 w-96 z-50 border-l border-gray-200 dark:border-gray-700 shadow-2xl bg-white dark:bg-gray-800'}`}
        >
            {!isInline && (
                <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center bg-indigo-600 text-white shadow-lg">
                    <div className="flex items-center gap-2">
                        <div className="p-1.5 bg-white/20 rounded-lg">
                            <ShoppingBagIcon className="w-5 h-5" />
                        </div>
                        <div>
                            <h2 className="font-bold leading-none">{t('shoppingAssistant_title')}</h2>
                            <span className="text-[10px] opacity-70 flex items-center gap-1 mt-1">
                                <span className="w-1.5 h-1.5 bg-green-400 rounded-full animate-pulse"></span>
                                {t('shoppingAssistant_subtitle')}
                            </span>
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        <button
                            onClick={handleClearHistory}
                            className="hover:bg-white/10 p-2 rounded-full transition-colors"
                            title={t('shoppingAssistant_clear_history')}
                            aria-label={t('shoppingAssistant_clear_history')}
                        >
                            <Trash2Icon className="w-4 h-4" />
                        </button>
                        <button
                            onClick={onClose}
                            className="hover:bg-white/10 p-2 rounded-full transition-colors"
                            title={t('shoppingAssistant_close')}
                            aria-label={t('shoppingAssistant_close')}
                        >
                            <XIcon className="w-6 h-6" />
                        </button>
                    </div>
                </div>
            )}

            <div className="flex-grow overflow-y-auto p-4 space-y-6 bg-gray-50 dark:bg-gray-900/50 custom-scrollbar">
                {messages.map((msg) => (
                    <div key={msg.id} className={`flex flex-col ${msg.role === 'user' ? 'items-end' : 'items-start'}`}>
                        {msg.role === 'model' && (
                            <div className="flex items-center gap-1.5 mb-1.5 ml-1">
                                <SparklesIcon className="w-3 h-3 text-indigo-500" />
                                <span className="text-[10px] font-bold text-gray-400 uppercase tracking-tighter">{t('shoppingAssistant_role_assistant')}</span>
                            </div>
                        )}

                        <div className={`max-w-[90%] p-3 rounded-2xl ${msg.role === 'user'
                            ? 'bg-indigo-600 text-white rounded-tr-none shadow-md shadow-indigo-600/10'
                            : msg.isError
                                ? 'bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 border border-red-100 dark:border-red-900/30 rounded-tl-none'
                                : 'bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-100 border border-gray-100 dark:border-gray-600 rounded-tl-none shadow-sm'
                            }`}>
                            {msg.text && <p className="text-sm leading-relaxed">{msg.text}</p>}
                            {msg.textKey && <p className="text-sm leading-relaxed">{t(msg.textKey as any, msg.textParams)}</p>}

                            {msg.payload?.type === 'products' && (
                                <div className="mt-4 -mx-1">
                                    <ProductCarousel
                                        products={msg.payload.data}
                                        onAddToBasket={onAddToBasket}
                                    />
                                </div>
                            )}

                            {msg.payload?.type === 'order_status' && (
                                <div className="mt-4">
                                    <OrderCard order={msg.payload.data} />
                                </div>
                            )}

                            {msg.payload?.type === 'social_media' && (
                                <div className="mt-4 space-y-3">
                                    {Object.entries(msg.payload.data as Record<string, string>).map(([platform, text]) => (
                                        <div key={platform} className="bg-gray-50 dark:bg-gray-800 p-3 rounded-xl border border-gray-100 dark:border-gray-700">
                                            <div className="flex justify-between items-center mb-2">
                                                <span className="text-[10px] font-bold uppercase text-indigo-600 dark:text-indigo-400">{platform}</span>
                                                <button
                                                    onClick={() => {
                                                        navigator.clipboard.writeText(text as string);
                                                        // Simple feedback could be added here
                                                    }}
                                                    className="p-1 hover:bg-indigo-100 dark:hover:bg-indigo-900/30 rounded"
                                                    title={t('shoppingAssistant_copy_caption')}
                                                >
                                                    <ShareIcon className="w-3.5 h-3.5 text-gray-400" />
                                                </button>
                                            </div>
                                            <p className="text-xs text-gray-600 dark:text-gray-300 italic">"{text}"</p>
                                        </div>
                                    ))}
                                    {onSaveAdvice && (
                                        <button
                                            onClick={() => onSaveAdvice({
                                                type: 'social_media_post',
                                                title: t('shoppingAssistant_social_media_title'),
                                                content: JSON.stringify(msg.payload?.data),
                                                createdAt: new Date().toISOString()
                                            })}
                                            className="w-full flex items-center justify-center gap-2 py-2 bg-indigo-50 dark:bg-indigo-900/20 text-indigo-600 dark:text-indigo-400 text-xs font-bold rounded-lg hover:bg-indigo-600 hover:text-white transition-all"
                                        >
                                            <CheckCircleIcon className="w-4 h-4" /> {t('shoppingAssistant_save_to_advice')}
                                        </button>
                                    )}
                                </div>
                            )}

                            {msg.payload?.type === 'booking' && (
                                <div className="mt-4 space-y-2">
                                    <div className="bg-indigo-600 text-white p-3 rounded-xl shadow-md">
                                        <div className="flex items-center gap-2 mb-2">
                                            <CalendarIcon className="w-4 h-4" />
                                            <span className="text-xs font-bold">{t(msg.payload.data.service as any)}</span>
                                        </div>
                                        <div className="space-y-1">
                                            {msg.payload.data.availableSlots.map((slot: string) => (
                                                <button
                                                    key={slot}
                                                    onClick={() => handleSendMessage(undefined, `Book ${slot}`)}
                                                    className="w-full text-left text-[10px] bg-white/10 hover:bg-white/20 p-2 rounded transition-colors flex justify-between items-center"
                                                >
                                                    {slot}
                                                    <span className="bg-white text-indigo-600 px-1.5 py-0.5 rounded-full font-bold">{t('shoppingAssistant_select_slot')}</span>
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                        <span className="text-[10px] text-gray-400 mt-1 mx-1">
                            {new Date(msg.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </span>
                    </div>
                ))}
                {isThinking && (
                    <div className="flex flex-col items-start">
                        <div className="flex items-center gap-1.5 mb-1.5 ml-1">
                            <SparklesIcon className="w-3 h-3 text-indigo-500 animate-pulse" />
                            <span className="text-[10px] font-bold text-gray-400 uppercase">{t('shoppingAssistant_thinking')}</span>
                        </div>
                        <div className="bg-white dark:bg-gray-700 p-3 rounded-2xl rounded-tl-none border border-gray-100 dark:border-gray-600">
                            <Spinner className="w-4 h-4 text-indigo-600" />
                        </div>
                    </div>
                )}
                <div ref={chatEndRef} />
            </div>

            <div className={`p-4 border-t border-gray-200 dark:border-gray-700 ${isInline ? 'bg-gray-50/50 dark:bg-gray-900/50' : 'bg-white dark:bg-gray-800'}`}>
                <div className="flex justify-between items-center mb-2">
                    <span className="text-[10px] text-gray-400 uppercase font-bold tracking-widest">{t('shoppingAssistant_input_label', 'Your Message')}</span>
                    {isInline && (
                        <button
                            onClick={handleClearHistory}
                            className="text-[10px] text-gray-400 hover:text-red-500 flex items-center gap-1 transition-colors"
                            title={t('shoppingAssistant_clear_history')}
                        >
                            <Trash2Icon className="w-3 h-3" /> {t('shoppingAssistant_clear_history')}
                        </button>
                    )}
                </div>
                <form onSubmit={handleSendMessage} className="relative group">
                    <input
                        type="text"
                        value={input}
                        onChange={(e) => setInput(e.target.value)}
                        placeholder={t('shoppingAssistant_input_placeholder')}
                        className="w-full pl-4 pr-12 py-3 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-2xl dark:text-white focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all outline-none text-sm group-hover:border-indigo-300 dark:group-hover:border-indigo-700"
                        disabled={isThinking}
                    />
                    <button
                        type="submit"
                        disabled={!input.trim() || isThinking}
                        className="absolute right-2 top-1/2 -translate-y-1/2 p-2 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 disabled:opacity-50 disabled:bg-gray-400 transition-all shadow-md shadow-indigo-600/20 active:scale-95"
                        title={t('shoppingAssistant_send_aria')}
                        aria-label={t('shoppingAssistant_send_aria')}
                    >
                        <SendIcon className="w-4 h-4" />
                    </button>
                </form>
                <div className="mt-3 flex flex-wrap gap-2">
                    <button
                        onClick={() => handleSendMessage(undefined, "Find summer dresses")}
                        className="text-[10px] font-medium bg-indigo-50 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400 px-2 py-1 rounded-full hover:bg-indigo-600 hover:text-white transition-colors"
                    >
                        "{t('shoppingAssistant_suggest_summer_dresses')}"
                    </button>
                    <button
                        onClick={() => handleSendMessage(undefined, "Where is my order?")}
                        className="text-[10px] font-medium bg-indigo-50 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400 px-2 py-1 rounded-full hover:bg-indigo-600 hover:text-white transition-colors"
                    >
                        "{t('shoppingAssistant_suggest_track_order')}"
                    </button>
                    <button
                        onClick={() => handleSendMessage(undefined, "Return policy")}
                        className="text-[10px] font-medium bg-indigo-50 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400 px-2 py-1 rounded-full hover:bg-indigo-600 hover:text-white transition-colors"
                    >
                        "{t('shoppingAssistant_suggest_return_policy')}"
                    </button>
                </div>
            </div>
        </motion.div>
    );
};
